<template>
  <HrPageLayout
    title="Holidays"
    subtitle="Manage public and company holidays"
    icon="🎄"
    :breadcrumb-items="[{ label: 'Company Settings' }, { label: 'Holidays' }]"
  >
    <template #header-actions>
      <button type="button" @click="createHoliday" class="hr-btn hr-btn-primary">
        <i class="mdi mdi-plus"></i> Add Holiday
      </button>
      <button type="button" @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
      <button type="button" @click="exportHolidays" class="hr-btn hr-btn-success">
        <i class="mdi mdi-file-export"></i> Export
      </button>
    </template>

    <div class="container-fluid">
      <!-- Statistics cards -->
            <div class="row">
              <!-- Total Holidays -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-primary h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-check font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Total Holidays</h6>
                        <h4 class="mb-0">{{ metrics.totalHolidays || 0 }}</h4>
                        <span class="text-muted font-size-12">
                          In {{ selectedYear }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Public Holidays -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-success h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-clock font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Public Holidays</h6>
                        <h4 class="mb-0">{{ metrics.publicHolidays || 0 }}</h4>
                        <span class="text-success font-size-12">Official holidays</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Fixed Holidays -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-info h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-month font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Fixed Holidays</h6>
                        <h4 class="mb-0">{{ metrics.fixedHolidays || 0 }}</h4>
                        <span class="text-info font-size-12">Recurring annually</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Months with Holidays -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-warning h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-range font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Months with Holidays</h6>
                        <h4 class="mb-0">{{ metrics.holidayMonths || 0 }}</h4>
                        <span class="text-warning font-size-12">Out of 12 months</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters and controls section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-3 mb-md-0">Holiday Filters</h5>
                      <div class="ms-auto">
                        <button type="button" class="btn btn-primary me-2" @click="createHoliday">
                          <i class="mdi mdi-plus me-1"></i> Create Holiday
                        </button>
                        <button type="button" class="btn btn-secondary me-2" @click="openImportModal">
                          <i class="mdi mdi-file-import me-1"></i> Import
                        </button>
                        <button type="button" class="btn btn-success me-2" @click="exportHolidays">
                          <i class="mdi mdi-file-export me-1"></i> Export
                        </button>
                        <button type="button" class="btn btn-info" @click="refreshData">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Year</label>
                        <select v-model="filters.year" class="form-select" @change="getHolidays">
                          <option v-for="year in yearOptions" :key="year" :value="year">
                            {{ year }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Month</label>
                        <select v-model="filters.month" class="form-select" @change="getHolidays">
                          <option value="">All Months</option>
                          <option v-for="(month, index) in months" :key="index" :value="index + 1">
                            {{ month }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Category</label>
                        <select v-model="filters.categoryId" class="form-select" @change="getHolidays">
                          <option value="">All Categories</option>
                          <option v-for="category in categories" :key="category.id" :value="category.id">
                            {{ category.category_name }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Type</label>
                        <select v-model="filters.isPublic" class="form-select" @change="getHolidays">
                          <option value="">All Types</option>
                          <option value="1">Public Holidays</option>
                          <option value="0">Non-public Holidays</option>
                        </select>
                      </div>

                      <div class="col-md-12">
                        <label class="form-label">Search</label>
                        <div class="position-relative">
                          <input
                              v-model="filters.search"
                              class="form-control"
                              placeholder="Search by holiday name or description..."
                              type="text"
                              @input="handleSearch"
                          >
                          <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Holiday Calendar & Upcoming Holidays -->
            <div class="row">
              <div class="col-xl-8 col-lg-7 mb-4">
                <div class="card">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">Holiday Calendar</h5>
                    <div class="btn-group" role="group">
                      <button :class="calendarView === 'month' ? 'btn-primary' : 'btn-outline-primary'"
                              class="btn btn-sm"
                              type="button"
                              @click="setCalendarView('month')">
                        Month
                      </button>
                      <button :class="calendarView === 'year' ? 'btn-primary' : 'btn-outline-primary'"
                              class="btn btn-sm"
                              type="button"
                              @click="setCalendarView('year')">
                        Year
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="calendar-container">
                      <!-- Calendar will be inserted here -->
                      <div id="holidays-calendar"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-4 col-lg-5 mb-4">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Upcoming Holidays</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="upcomingHolidays.length > 0" class="upcoming-holidays-list">
                      <div v-for="holiday in upcomingHolidays" :key="holiday.id" class="upcoming-holiday-item mb-3">
                        <div :class="{'bg-light': isHolidayToday(holiday.holiday_date)}"
                             class="d-flex align-items-center p-3 border rounded">
                          <div class="holiday-date-badge me-3 text-center">
                            <span class="date-month">{{ formatDateMonth(holiday.holiday_date) }}</span>
                            <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(holiday.holiday_date) }}</span>
                          </div>
                          <div class="holiday-details flex-grow-1">
                            <h6 class="holiday-title mb-1">{{ holiday.holiday_name }}</h6>
                            <div class="holiday-meta text-muted small">
                              <span><i class="mdi mdi-calendar-check me-1"></i>{{ holiday.categories || 'No category' }}</span>
                              <span class="ms-2"><i class="mdi mdi-earth me-1"></i>{{ holiday.country_code }}</span>
                            </div>
                          </div>
                          <div v-if="isHolidayToday(holiday.holiday_date)" class="holiday-badge ms-2">
                            <span class="badge bg-success">Today</span>
                          </div>
                          <div v-else-if="isHolidaySoon(holiday.holiday_date)" class="holiday-badge ms-2">
                            <span class="badge bg-warning">Soon</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-calendar font-size-48 text-muted"></i>
                      <p class="mt-2">No upcoming holidays</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Holidays List Table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Holidays List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading overlay -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading holidays data...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!holidays.length" class="text-center p-5">
                      <i class="mdi mdi-calendar-blank font-size-48 text-muted"></i>
                      <h5 class="mt-2">No holidays found</h5>
                      <p class="text-muted">Try adjusting your filters or create a new holiday</p>
                      <button class="btn btn-sm btn-outline-primary mt-2" @click="resetFilters">Reset Filters</button>
                    </div>

                    <!-- Holidays table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover table-bordered align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Holiday Name</th>
                          <th>Date</th>
                          <th>Details</th>
                          <th>Category</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="holiday in holidays" :key="holiday.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div :class="getHolidayIconColor(holiday)" class="avatar-xs me-2 rounded p-1 text-center">
                                <i class="mdi mdi-calendar font-size-18 text-white"></i>
                              </div>
                              <div>
                                <h5 class="font-size-14 mb-0">{{ holiday.holiday_name }}</h5>
                                <p class="text-muted mb-0 font-size-12">{{ truncateText(holiday.description, 40) }}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span class="d-block font-weight-medium">{{ formatDate(holiday.holiday_date) }}</span>
                              <span v-if="holiday.is_fixed == 1" class="badge bg-info me-1">Fixed</span>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <div>
                                  <span :class="holiday.is_public == 1 ? 'bg-success' : 'bg-secondary'" class="badge">
                                    {{ holiday.is_public == 1 ? 'Public' : 'Non-Public' }}
                                  </span>
                              </div>
                              <div class="mt-1">
                                <span class="text-muted small"><i class="mdi mdi-earth me-1"></i>{{holiday.country_code}}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div v-if="holiday.categories && holiday.categories.length">
    <span v-for="(category, index) in getCategoryList(holiday.categories)"
          :key="index"
          class="badge"
          :style="{ backgroundColor: getCategoryColor(holiday.category_colors, index) }">
      {{ category }}
    </span>
                            </div>
                            <div v-else>
                              <span class="badge bg-light text-dark">No category</span>
                            </div>
                          </td>

                          <td class="text-center">
                            <div class="d-flex justify-content-center">
                              <button
                                  class="btn btn-sm btn-info me-2"
                                  title="View Details"
                                  @click="viewHolidayDetails(holiday)"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  class="btn btn-sm btn-primary me-2"
                                  title="Edit Holiday"
                                  @click="editHoliday(holiday)"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  class="btn btn-sm btn-danger"
                                  title="Delete Holiday"
                                  @click="deleteHoliday(holiday)"
                              >
                                <i class="mdi mdi-delete"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                  </div>
                </div>
              </div>
            </div>
    </div>

  <!-- Holiday Create/Edit Modal -->
  <div v-if="showHolidayModal" class="holiday-inline-modal-overlay" @click.self="closeHolidayModal">
    <div class="holiday-inline-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="holidayModalLabel" class="modal-title">{{ isEditMode ? 'Edit Holiday' : 'Create New Holiday' }}</h5>
          <button aria-label="Close" class="btn-close" type="button" @click="closeHolidayModal"></button>
        </div>
        <div class="modal-body">
          <form id="holidayForm" @submit.prevent="submitHoliday">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Holiday Name <span class="text-danger">*</span></label>
                <input v-model="holidayForm.holidayName" class="form-control" required type="text">
              </div>

              <div class="col-md-6">
                <label class="form-label">Holiday Date <span class="text-danger">*</span></label>
                <input v-model="holidayForm.holidayDate" class="form-control" required type="date">
              </div>

              <div class="col-md-6">
                <label class="form-label">Country Code</label>
                <select v-model="holidayForm.countryCode" class="form-select">
                  <option value="KE">Kenya (KE)</option>
                  <option value="US">United States (US)</option>
                  <option value="GB">United Kingdom (GB)</option>
                  <option value="CA">Canada (CA)</option>
                  <option value="AU">Australia (AU)</option>
                  <option value="IN">India (IN)</option>
                  <option value="Other">Other</option>
                </select>
              </div>



              <div class="col-md-12">
                <label class="form-label">Description</label>
                <textarea v-model="holidayForm.description" class="form-control" rows="3"></textarea>
              </div>

              <div class="col-md-12">
                <label class="form-label">Categories</label>
                <div class="card p-3">
                  <div class="category-selection">
                    <div v-for="category in categories" :key="category.id" class="form-check mb-2">
                      <input
                          :id="`category-${category.id}`"
                          v-model="holidayForm.categories"
                          :value="category.id"
                          class="form-check-input"
                          type="checkbox"
                      >
                      <label :for="`category-${category.id}`" class="form-check-label">
                        <span :style="{ backgroundColor: category.color_code }" class="color-dot"></span>
                        {{ category.category_name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-check form-switch mt-2">
                  <input
                      id="isPublicSwitch"
                      v-model="holidayForm.isPublic"
                      class="form-check-input"
                      type="checkbox"
                  >
                  <label class="form-check-label" for="isPublicSwitch">
                    Public Holiday
                  </label>
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-check form-switch mt-2">
                  <input
                      id="isFixedSwitch"
                      v-model="holidayForm.isFixed"
                      class="form-check-input"
                      type="checkbox"
                  >
                  <label class="form-check-label" for="isFixedSwitch">
                    Fixed Holiday (recurs annually)
                  </label>
                </div>
              </div>

              <div class="col-md-12 mt-4">
                <div class="form-check form-switch">
                  <input
                      id="specifyDepartmentsSwitch"
                      v-model="holidayForm.specifyDepartments"
                      class="form-check-input"
                      type="checkbox"
                  >
                  <label class="form-check-label" for="specifyDepartmentsSwitch">
                    Specific Departments/Locations
                  </label>
                </div>
              </div>

              <div v-if="holidayForm.specifyDepartments" class="col-md-6">
                <label class="form-label">Departments</label>
                <select v-model="holidayForm.departmentIds" class="form-select" multiple>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.departmentName }}
                  </option>
                </select>
                <small class="text-muted">Hold Ctrl/Cmd to select multiple</small>
              </div>

              <div v-if="holidayForm.specifyDepartments" class="col-md-6">
                <label class="form-label">Locations</label>
                <select v-model="holidayForm.locationIds" class="form-select" multiple>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">
                    {{ loc.locationName }}
                  </option>
                </select>
                <small class="text-muted">Hold Ctrl/Cmd to select multiple</small>
              </div>

              <div v-if="holidayForm.specifyDepartments" class="col-md-6">
                <div class="form-check form-switch mt-4">
                  <input
                      id="halfDaySwitch"
                      v-model="holidayForm.halfDay"
                      class="form-check-input"
                      type="checkbox"
                  >
                  <label class="form-check-label" for="halfDaySwitch">
                    Half-day Holiday
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" type="button" @click="closeHolidayModal">Close</button>
          <button :disabled="submitLoading" class="btn btn-primary" type="button" @click="submitHoliday">
            <span v-if="submitLoading" aria-hidden="true" class="spinner-border spinner-border-sm me-1"
                  role="status"></span>
            {{ isEditMode ? 'Update Holiday' : 'Create Holiday' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Holiday Details Modal -->
  <div id="holidayDetailsModal" aria-hidden="true" aria-labelledby="holidayDetailsModalLabel" class="modal fade"
       tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="holidayDetailsModalLabel" class="modal-title">Holiday Details</h5>
          <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" type="button"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedHoliday">
            <div class="card mb-4">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <h4 class="font-size-18 mb-2">{{ selectedHoliday.holiday_name }}</h4>
                    <div class="d-flex align-items-center my-2">
                      <span :class="selectedHoliday.is_public == 1 ? 'bg-success' : 'bg-secondary'" class="badge me-2">
                        {{ selectedHoliday.is_public == 1 ? 'Public Holiday' : 'Non-Public Holiday' }}
                      </span>
                      <span v-if="selectedHoliday.is_fixed == 1" class="badge bg-info me-2">
                        Fixed (Annual)
                      </span>
                    </div>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-earth me-1"></i> Country: {{ selectedHoliday.country_code }}
                    </p>
                  </div>
                  <div class="col-md-4 text-end">
                    <div class="mb-2">
                      <span class="badge bg-light text-dark">Date: {{ formatDate(selectedHoliday.holiday_date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-header bg-transparent">
                <h5 class="card-title mb-0">Description</h5>
              </div>
              <div class="card-body">
                <p>{{ selectedHoliday.description || 'No description available.' }}</p>
              </div>
            </div>

            <div class="row">
              <!-- Categories -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Categories</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="holidayCategories && holidayCategories.length > 0">
                      <div class="d-flex flex-wrap gap-2">
                        <span v-for="category in holidayCategories" :key="category.id"
                              :style="{ backgroundColor: category.color_code }"
                              class="badge p-2">
                          {{ category.category_name }}
                        </span>
                      </div>
                    </div>
                    <div v-else class="text-muted">
                      No categories assigned.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Observations -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Specific Observations</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="holidayObservations && holidayObservations.length > 0">
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                          <tr>
                            <th>Department</th>
                            <th>Location</th>
                            <th>Half Day</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(obs, index) in holidayObservations" :key="index">
                            <td>{{ getDepartmentName(obs.department_id) || 'All' }}</td>
                            <td>{{ getLocationName(obs.location_id) || 'All' }}</td>
                            <td>{{ obs.half_day == 1 ? 'Yes' : 'No' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-muted">
                      This holiday applies to all departments and locations.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">Close</button>
          <button v-if="selectedHoliday" class="btn btn-primary" type="button" @click="editHoliday(selectedHoliday)">
            <i class="mdi mdi-pencil me-1"></i> Edit
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Import Holidays Modal -->
  <div id="importHolidaysModal" aria-hidden="true" aria-labelledby="importHolidaysModalLabel" class="modal fade"
       tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="importHolidaysModalLabel" class="modal-title">Import Holidays</h5>
          <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" type="button"></button>
        </div>
        <div class="modal-body">
          <form id="importForm" @submit.prevent="importHolidays">
            <div class="mb-3">
              <label class="form-label">Upload CSV File <span class="text-danger">*</span></label>
              <input
                  ref="importFile"
                  accept=".csv"
                  class="form-control"
                  required
                  type="file"
              >
              <small class="text-muted">
                File must be in CSV format with columns: holiday_name, holiday_date (YYYY-MM-DD), description, is_fixed
                (0/1), is_public (0/1), country_code, categories (comma-separated)
              </small>
            </div>
            <div class="mb-3">
              <a class="text-primary" href="#" @click.prevent="downloadTemplate">
                <i class="mdi mdi-download me-1"></i> Download template
              </a>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">Close</button>
          <button :disabled="importLoading" class="btn btn-primary" form="importForm" type="submit">
            <span v-if="importLoading" aria-hidden="true" class="spinner-border spinner-border-sm me-1"
                  role="status"></span>
            Import Holidays
          </button>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import {toast, authUrl, execute} from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import FullCalendar from '@fullcalendar/core';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

export default {
  name: "Holidays",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      submitLoading: false,
      importLoading: false,
      holidays: [],
      upcomingHolidays: [],
      categories: [],
      departments: [],
      locations: [],
      metrics: {
        totalHolidays: 0,
        publicHolidays: 0,
        fixedHolidays: 0,
        holidayMonths: 0
      },
      filters: {
        year: new Date().getFullYear(),
        month: "",
        categoryId: "",
        isPublic: "",
        search: ""
      },
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      selectedHoliday: null,
      holidayCategories: [],
      holidayObservations: [],
      holidayForm: {
        holidayName: "",
        holidayDate: "",
        description: "",
        countryCode: "KE",
        isPublic: true,
        isFixed: false,
        categories: [],
        specifyDepartments: false,
        departmentIds: [],
        locationIds: [],
        halfDay: false
      },
      isEditMode: false,
      calendarView: 'month',
      calendar: null,
      showHolidayModal: false
    };
  },
  computed: {
    selectedYear() {
      return this.filters.year;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      return [
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2
      ];
    }
  },
  methods: {
    clearHolidayModalBackdrops() {
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('overflow');
    },
    getHolidays() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getHolidays");
      data.append("year", this.filters.year);

      if (this.filters.month) {
        data.append("month", this.filters.month);
      }

      if (this.filters.categoryId) {
        data.append("categoryId", this.filters.categoryId);
      }

      if (this.filters.isPublic !== "") {
        data.append("isPublic", this.filters.isPublic);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.holidays = res.data.data.holidays || [];
              this.metrics = res.data.data.metrics || {};
              this.upcomingHolidays = res.data.data.upcomingHolidays || [];

              if (res.data.data.categories) {
                this.categories = res.data.data.categories;
              }

              // Update calendar events
              this.updateCalendarEvents();
            } else {
              console.error("Error fetching holidays:", res.data.message);
              this.showToast("error", "Failed to load holidays data: " + res.data.message);
              this.holidays = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching holidays");
            this.holidays = [];
            this.metrics = {};
          });
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getLocations() {
      const data = new FormData();
      data.append("function", "getLocations");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.locations = res.data.data.locations || [];
            } else {
              console.error("Error fetching locations:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getHolidayCategories() {
      const data = new FormData();
      data.append("function", "getHolidayCategories");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.categories = res.data.data.categories || [];
            } else {
              console.error("Error fetching holiday categories:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    refreshData() {
      this.getHolidays();
    },

    resetFilters() {
      this.filters = {
        year: new Date().getFullYear(),
        month: "",
        categoryId: "",
        isPublic: "",
        search: ""
      };
      this.getHolidays();
    },

    handleSearch: _.debounce(function () {
      this.getHolidays();
    }, 500),

    closeHolidayModal() {
      this.showHolidayModal = false;
      this.submitLoading = false;
      this.clearHolidayModalBackdrops();
    },

    createHoliday() {
      this.isEditMode = false;
      this.holidayForm = {
        holidayName: "",
        holidayDate: new Date().toISOString().split('T')[0],
        description: "",
        countryCode: "KE",
        isPublic: true,
        isFixed: false,
        categories: [],
        specifyDepartments: false,
        departmentIds: [],
        locationIds: [],
        halfDay: false
      };

      this.clearHolidayModalBackdrops();
      this.showHolidayModal = true;
    },

    editHoliday(holiday) {
      this.isEditMode = true;
      this.selectedHoliday = holiday;

      // Close details modal if open
      const detailsEl = document.getElementById('holidayDetailsModal');
      if (detailsEl && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const detailsModal = bootstrap.Modal.getInstance(detailsEl);
        if (detailsModal) {
          detailsModal.hide();
        }
      }

      // Get holiday details for editing
      this.getHolidayDetails(holiday.id, () => {
        // After getting details, prepare form and show modal
        this.prepareEditForm();
      });
    },

    prepareEditForm() {
      if (!this.selectedHoliday) return;

      // Parse category IDs
      let categoryIds = [];
      if (this.selectedHoliday.category_ids) {
        categoryIds = Array.isArray(this.selectedHoliday.category_ids)
            ? this.selectedHoliday.category_ids
            : this.selectedHoliday.category_ids.split(',').map(id => parseInt(id.trim()));
      }

      // Check if there are any specific observations
      const hasObservations = this.holidayObservations && this.holidayObservations.length > 0;

      // Extract department and location IDs from observations
      let departmentIds = [];
      let locationIds = [];
      let halfDay = false;

      if (hasObservations) {
        const firstObs = this.holidayObservations[0];
        halfDay = firstObs.half_day == 1;

        departmentIds = this.holidayObservations
            .filter(obs => obs.department_id)
            .map(obs => obs.department_id);

        locationIds = this.holidayObservations
            .filter(obs => obs.location_id)
            .map(obs => obs.location_id);
      }

      // Prepare form data
      this.holidayForm = {
        holidayId: this.selectedHoliday.id,
        holidayName: this.selectedHoliday.holiday_name,
        holidayDate: this.selectedHoliday.holiday_date,
        description: this.selectedHoliday.description || "",
        countryCode: this.selectedHoliday.country_code || "US",
        isPublic: this.selectedHoliday.is_public == 1,
        isFixed: this.selectedHoliday.is_fixed == 1,
        categories: categoryIds,
        specifyDepartments: hasObservations,
        departmentIds: departmentIds,
        locationIds: locationIds,
        halfDay: halfDay
      };

      // Show the modal
      this.clearHolidayModalBackdrops();
      this.showHolidayModal = true;
    },

    viewHolidayDetails(holiday) {
      this.getHolidayDetails(holiday.id, () => {
        const el = document.getElementById('holidayDetailsModal');
        if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modal = new bootstrap.Modal(el);
          modal.show();
        }
      });
    },

    getHolidayDetails(holidayId, callback) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getHolidayDetails");
      data.append("holidayId", holidayId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.selectedHoliday = res.data.data.holiday;
              this.holidayCategories = res.data.data.categories || [];
              this.holidayObservations = res.data.data.observations || [];

              if (callback) callback();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching holiday details");
          });
    },

    submitHoliday() {
      // Validate form
      if (!this.validateHolidayForm()) {
        return;
      }

      this.submitLoading = true;
      const data = new FormData();

      // Determine if creating or updating
      if (this.isEditMode) {
        data.append("function", "updateHoliday");
        data.append("holidayId", this.holidayForm.holidayId);
      } else {
        data.append("function", "createHoliday");
      }

      // Add form fields
      data.append("holidayName", this.holidayForm.holidayName);
      data.append("holidayDate", this.holidayForm.holidayDate);
      data.append("description", this.holidayForm.description);
      data.append("countryCode", this.holidayForm.countryCode);
      data.append("isPublic", this.holidayForm.isPublic ? 1 : 0);
      data.append("isFixed", this.holidayForm.isFixed ? 1 : 0);

      // Add categories if selected
      if (this.holidayForm.categories.length > 0) {
        data.append("categories", JSON.stringify(this.holidayForm.categories));
      }

      // Add department/location specific details if enabled
      if (this.holidayForm.specifyDepartments) {
        if (this.holidayForm.departmentIds.length > 0) {
          data.append("departmentIds", JSON.stringify(this.holidayForm.departmentIds));
        }

        if (this.holidayForm.locationIds.length > 0) {
          data.append("locationIds", JSON.stringify(this.holidayForm.locationIds));
        }

        data.append("halfDay", this.holidayForm.halfDay ? 1 : 0);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              this.closeHolidayModal();
              this.showToast("success", res.data.message);
              this.getHolidays(); // Refresh the data
            } else {
              this.showToast("error", res.data.message || "Failed to save holiday");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when saving holiday");
          });
    },

    validateHolidayForm() {
      if (!this.holidayForm.holidayName.trim()) {
        this.showToast("error", "Holiday name is required");
        return false;
      }

      if (!this.holidayForm.holidayDate) {
        this.showToast("error", "Holiday date is required");
        return false;
      }

      if (this.holidayForm.specifyDepartments &&
          this.holidayForm.departmentIds.length === 0 &&
          this.holidayForm.locationIds.length === 0) {
        this.showToast("error", "Please select at least one department or location");
        return false;
      }

      return true;
    },

    deleteHoliday(holiday) {
      if (confirm(`Are you sure you want to delete the holiday "${holiday.holiday_name}"?`)) {
        const data = new FormData();
        data.append("function", "deleteHoliday");
        data.append("holidayId", holiday.id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);
                this.getHolidays(); // Refresh the data
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when deleting holiday");
            });
      }
    },

    openImportModal() {
      const el = document.getElementById('importHolidaysModal');
      if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const modal = new bootstrap.Modal(el);
        modal.show();
      }
    },

    importHolidays() {
      if (!this.$refs.importFile.files[0]) {
        this.showToast("error", "Please select a CSV file");
        return;
      }

      this.importLoading = true;
      const data = new FormData();
      data.append("function", "importHolidays");
      data.append("holidaysFile", this.$refs.importFile.files[0]);

      execute(data, authUrl)
          .then((res) => {
            this.importLoading = false;

            if (res.data.success) {
              // Close the modal
              const el = document.getElementById('importHolidaysModal');
              if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                const modal = bootstrap.Modal.getInstance(el);
                if (modal) {
                  modal.hide();
                }
              }

              this.showToast("success", res.data.message);
              this.getHolidays(); // Refresh the data

              // Show import results if available
              if (res.data.data && res.data.data.errors && res.data.data.errors.length > 0) {
                console.warn("Import errors:", res.data.data.errors);
                this.showToast("warning", `Some holidays could not be imported: ${res.data.data.errors.length} errors`);
              }
            } else {
              this.showToast("error", res.data.message || "Failed to import holidays");
            }
          })
          .catch((error) => {
            this.importLoading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when importing holidays");
          });
    },

    exportHolidays() {
      // Redirect to export URL
      const url = `${authUrl}?function=exportHolidays&year=${this.filters.year}`;
      window.open(url, '_blank');
    },

    downloadTemplate() {
      // Create CSV template content
      const headers = [
        'holiday_name',
        'holiday_date',
        'description',
        'is_fixed',
        'is_public',
        'country_code',
        'categories'
      ];

      const sampleData = [
        [
          'New Year\'s Day',
          `${new Date().getFullYear()}-01-01`,
          'New Year\'s Day celebration',
          '1',
          '1',
          'US',
          'Public Holiday'
        ],
        [
          'Independence Day',
          `${new Date().getFullYear()}-07-04`,
          'Independence Day celebration',
          '1',
          '1',
          'US',
          'Public Holiday,Cultural Holiday'
        ]
      ];

      let csvContent = headers.join(',') + '\n';
      sampleData.forEach(row => {
        csvContent += row.join(',') + '\n';
      });

      // Create and download file
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'holidays_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    initCalendar() {
      // Initialize FullCalendar
      const calendarEl = document.getElementById('holidays-calendar');
      if (!calendarEl) return;

      try {
        // Create a calendar instance using the proper import method
        this.calendar = new Calendar(calendarEl, {
          plugins: [dayGridPlugin, interactionPlugin],
          initialView: 'dayGridMonth',
          headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: ''
          },
          events: [],
          eventClick: this.handleCalendarEventClick,
          eventTimeFormat: {
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          },
          height: 'auto',
          // Add additional options as needed
          dayMaxEvents: true, // Allow "more" link when too many events
          weekNumbers: false,
          navLinks: true, // Can click day/week names to navigate views
          editable: false, // Don't allow event dragging
          selectable: false, // Don't allow date selection
          businessHours: false, // Don't show business hours
          nowIndicator: true, // Show indicator for current time
          // Customize event rendering
          eventDidMount: (info) => {
            // Add tooltips or additional styling to events
            if (info.event.extendedProps.isPublic) {
              info.el.classList.add('fc-event-public');
            }
          }
        });

        // Render the calendar
        this.calendar.render();

        // Load calendar events
        this.updateCalendarEvents();

        console.log('Calendar initialized successfully');
      } catch (error) {
        console.error('Error initializing calendar:', error);
        // Provide fallback UI or error message
        calendarEl.innerHTML = '<div class="alert alert-danger">Failed to load calendar. Please try refreshing the page.</div>';
      }
    },

    updateCalendarEvents() {
      if (!this.calendar) return;

      const data = new FormData();
      data.append("function", "getHolidayCalendar");
      data.append("year", this.filters.year);
      data.append("month", this.filters.month || new Date().getMonth() + 1);
      data.append("view", this.calendarView);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Clear existing events
              this.calendar.removeAllEvents();

              // Add new events
              if (res.data.data.events) {
                this.calendar.addEventSource(res.data.data.events);
              }

              // Update calendar date
              if (this.calendarView === 'month' && this.filters.month) {
                this.calendar.gotoDate(`${this.filters.year}-${this.filters.month.toString().padStart(2, '0')}-01`);
              } else {
                this.calendar.gotoDate(`${this.filters.year}-01-01`);
              }

              // Update view
              if (this.calendarView === 'year') {
                this.calendar.changeView('dayGridYear');
              } else {
                this.calendar.changeView('dayGridMonth');
              }
            } else {
              console.error("Error fetching calendar events:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    setCalendarView(view) {
      this.calendarView = view;
      this.updateCalendarEvents();
    },

    handleCalendarEventClick(info) {
      const holidayId = info.event.id;
      if (holidayId) {
        this.getHolidayDetails(holidayId, () => {
          const el = document.getElementById('holidayDetailsModal');
          if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
            const modal = new bootstrap.Modal(el);
            modal.show();
          }
        });
      }
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {month: 'short'});
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    isHolidayToday(dateString) {
      if (!dateString) return false;

      const today = new Date();
      const holidayDate = new Date(dateString);

      return today.getFullYear() === holidayDate.getFullYear() &&
          today.getMonth() === holidayDate.getMonth() &&
          today.getDate() === holidayDate.getDate();
    },

    isHolidaySoon(dateString) {
      if (!dateString) return false;

      const today = new Date();
      const holidayDate = new Date(dateString);
      const diffTime = holidayDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays > 0 && diffDays <= 7;
    },

    getHolidayIconColor(holiday) {
      if (!holiday) return 'bg-secondary';

      // If has categories with colors, use the first one
      if (holiday.category_colors) {
        const colors = holiday.category_colors.split(',');
        if (colors.length > 0) {
          // We cannot directly use the color code as a background class
          // So we'll map common colors to Bootstrap classes
          const colorMap = {
            '#e74c3c': 'bg-danger',
            '#9b59b6': 'bg-purple',
            '#f39c12': 'bg-warning',
            '#2ecc71': 'bg-success',
            '#3498db': 'bg-primary'
          };

          // Check if the color exists in our map
          if (colorMap[colors[0]]) {
            return colorMap[colors[0]];
          }
        }
      }

      // Default coloring based on public/fixed status
      if (holiday.is_public == 1) {
        return 'bg-success';
      } else if (holiday.is_fixed == 1) {
        return 'bg-info';
      }

      return 'bg-secondary';
    },

    getCategoryList(categories) {
      if (!categories) return [];
      return categories.split(',');
    },

    getCategoryColor(colorString, index) {
      if (!colorString) return '#3498db';

      const colors = colorString.split(',');
      return colors[index] || colors[0] || '#3498db';
    },

    getDepartmentName(departmentId) {
      if (!departmentId) return null;

      const department = this.departments.find(d => d.id == departmentId);
      return department ? department.departmentName : departmentId;
    },

    getLocationName(locationId) {
      if (!locationId) return null;

      const location = this.locations.find(l => l.id == locationId);
      return location ? location.locationName : locationId;
    },

    truncateText(text, maxLength) {
      if (!text) return '';

      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  watch: {
    'filters.year': function () {
      this.getHolidays();
    },
    'filters.month': function () {
      this.updateCalendarEvents();
    },
    calendarView: function () {
      this.updateCalendarEvents();
    }
  },
  beforeUnmount() {
    // Clean up calendar instance
    if (this.calendar) {
      this.calendar.destroy();
    }
    this.clearHolidayModalBackdrops();
  },
  mounted() {
    this.clearHolidayModalBackdrops();
    this.getDepartments();
    this.getLocations();
    this.getHolidayCategories();
    this.getHolidays();
    this.initCalendar();

  }
};
</script>

<style scoped>
/* Card hover effects */
.card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Holiday date badge */
.holiday-date-badge {
  min-width: 45px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Category selection scrollable area */
.category-selection {
  max-height: 200px;
  overflow-y: auto;
}

/* Color dot for category */
.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 5px;
}

/* Calendar container */
.calendar-container {
  height: 600px;
}

/* Custom class for purple badges */
.bg-purple {
  background-color: #9b59b6;
  color: white;
}

.holiday-inline-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 30000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.holiday-inline-modal {
  width: min(980px, 96vw);
  max-height: 92vh;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.holiday-inline-modal .modal-content {
  border: 0;
  border-radius: 10px;
}

.holiday-inline-modal .modal-header,
.holiday-inline-modal .modal-body,
.holiday-inline-modal .modal-footer {
  padding-left: 24px;
  padding-right: 24px;
}

.holiday-inline-modal .modal-header {
  padding-top: 20px;
  padding-bottom: 14px;
}

.holiday-inline-modal .modal-body {
  padding-top: 16px;
  padding-bottom: 16px;
}

.holiday-inline-modal .modal-footer {
  padding-top: 14px;
  padding-bottom: 20px;
}
</style>

<style>
/* Ensure Holidays modals stay above any page overlays */
#holidayModal,
#holidayDetailsModal,
#importHolidaysModal {
  z-index: 20050 !important;
}

#holidayModal .modal-dialog,
#holidayDetailsModal .modal-dialog,
#importHolidaysModal .modal-dialog {
  z-index: 20060 !important;
}

#holidayModal .modal-content,
#holidayDetailsModal .modal-content,
#importHolidaysModal .modal-content {
  position: relative;
  z-index: 20070 !important;
  pointer-events: auto !important;
}

body .modal-backdrop {
  z-index: 20040 !important;
}
</style>

<style src="@/assets/css/hr-page-shared.css"></style>